import { useForm } from "@tanstack/react-form";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type {
  BrowserLoginMetadata,
  BrowserLoginHold,
  SaveBrowserLogin,
  ForgetBrowserLogin,
} from "@/types/browserLogin";

export type BrowserLoginClient = {
  metadata: (origin: string, signal: AbortSignal) => Promise<BrowserLoginMetadata>;
  save: (request: SaveBrowserLogin) => Promise<void>;
  forget: (request: ForgetBrowserLogin) => Promise<void>;
};

/** Credentials live only in the form and the in-flight request, never mutation variables. */
export function useBrowserLoginForm(
  client: BrowserLoginClient,
  origin: string,
  hold: BrowserLoginHold | undefined,
  onSaved: () => void,
) {
  const secrets = useRef<{ username: string; password: string } | undefined>(undefined);
  const queryClient = useQueryClient();
  const metadata = useQuery(
    queryOptions({
      queryKey: ["browser-login", origin],
      queryFn: ({ signal }) => client.metadata(origin, signal),
      staleTime: 0,
      refetchOnMount: "always",
    }),
  );
  const finish = async () => {
    await queryClient.invalidateQueries({ queryKey: ["website-sign-ins"] });
    await queryClient.invalidateQueries({ queryKey: ["browser-login", origin] });
    onSaved();
  };
  const save = useMutation({
    mutationFn: async () => {
      const values = secrets.current;
      const current = metadata.data;
      if (!values || !current || metadata.isError) throw new Error("Refresh this sign-in form.");
      try {
        return await client.save({ ...current, ...values, hold });
      } finally {
        secrets.current = undefined;
      }
    },
    onSuccess: finish,
    onError: () => {
      void metadata.refetch();
    },
    retry: false,
    gcTime: 0,
  });
  const forget = useMutation({
    mutationFn: async () => {
      const current = metadata.data;
      if (!current?.itemId || current.version === undefined || metadata.isError)
        throw new Error("Refresh this sign-in form.");
      await client.forget({ origin, itemId: current.itemId, version: current.version });
    },
    onSuccess: finish,
    onError: () => {
      void metadata.refetch();
    },
    retry: false,
  });
  const form = useForm({
    defaultValues: { username: "", password: "" },
    onSubmit: async ({ value, formApi }) => {
      secrets.current = { ...value };
      formApi.reset();
      try {
        await save.mutateAsync();
      } catch {
        /* The form shows a fixed error without serializing a request. */
      } finally {
        secrets.current = undefined;
      }
    },
  });
  useEffect(() => {
    secrets.current = undefined;
    form.reset();
    return () => {
      secrets.current = undefined;
      form.reset();
    };
  }, [form, origin, hold?.controlId]);
  const pending = save.isPending || forget.isPending;
  let submitLabel = "Save login";
  if (hold) submitLabel = "Save and continue";
  if (pending) submitLabel = "Saving…";
  return { form, save, forget, metadata, pending, submitLabel };
}
